import { EXPLORE_ROUTES } from "../exploreRoutes";
import { GAMIFICATION_ROUTES } from "./gamificationRoutes";
import React, { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import HomeDashboardLayout from "../HomeDashboardLayout";
import StartGamificationHeader from "./StartGamificationHeader";
import BattleCard from "./BattleCard";
import GamificationActivityDialog from "./GamificationActivityDialog";
import { useGamificationAttemptActions } from "../../hooks/useGamificationAttemptActions";
import CoinChooseOption from "./CoinChooseOption";
import { useAttemptedGamificationList } from "../../hooks/useAttemptedGamificationList";
import MyWalletSidebar from "../MyWalletSidebar";
import {
  buildGamificationBackTarget,
  buildGamificationFlowState,
  getStoredGamificationFlowState,
  persistGamificationFlowState,
} from "./gamificationFlowState";
import "../../dashboard/Dashboard.css";
import "../HomeDashboard.css";
import "../../questionBank/QuestionBankList.css";
import HeaderProfileSection from "../HeaderProfileSection";

const DEFAULT_STATE_ID = 13;
const DEFAULT_CATEGORY_ID = 6;

function GamificationHeaderShimmer() {
  return (
    <div className="qb-body" aria-hidden="true">
      <div className="d-flex align-items-center justify-content-center">
        <div className="rapid-fire-card">
          <span className="dashboard-skeleton dashboard-skeleton--line dashboard-skeleton--title" style={{ width: "32%", height: 34 }} />
          <span className="dashboard-skeleton dashboard-skeleton--line" style={{ width: "42%", marginTop: 12 }} />
          <div className="game-battle-stats-container">
            {Array.from({ length: 4 }).map((_, index) => (
              <span key={index} className="dashboard-skeleton dashboard-skeleton--button" style={{ width: index === 3 ? 150 : 120, height: 48, borderRadius: 999 }} />
            ))}
          </div>
          <span className="dashboard-skeleton dashboard-skeleton--line" style={{ width: "100%", height: 62, borderRadius: 22, marginTop: 6 }} />
        </div>
      </div>
    </div>
  );
}

const StartGamification = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  const flowState = useMemo(() => {
    const invitation = new URLSearchParams(location.search);
    const invitedStateId = Number(invitation.get("stateId") || 0);
    const invitedCategoryId = Number(invitation.get("categoryId") || 0);
    return buildGamificationFlowState({
      ...(location.state || {}),
      ...(invitedStateId > 0 ? { state_id: invitedStateId, selectedState: { state_id: invitedStateId } } : {}),
      ...(invitedCategoryId > 0 ? { category_id: invitedCategoryId, selectedCategory: { category_id: invitedCategoryId } } : {}),
    }, getStoredGamificationFlowState());
  }, [location.state, location.search]);
  const selectedStateId = Number(flowState?.selectedState?.state_id || flowState?.state_id || DEFAULT_STATE_ID);
  const selectedCategoryId = Number(
    flowState?.selectedCategory?.category_id || flowState?.category_id || DEFAULT_CATEGORY_ID
  );
  const previousBackTarget = useMemo(
    () => {
      const target = flowState?.gamificationBackTarget;

      // Stored state can originate from a child gamification screen. Never
      // allow the entry screen to navigate back to itself.
      return target?.pathname && target.pathname !== GAMIFICATION_ROUTES.start
        ? target
        : buildGamificationBackTarget(EXPLORE_ROUTES.categories, flowState);
    },
    [flowState]
  );
  const [isCoinOptionsOpen, setIsCoinOptionsOpen] = useState(false);
  const handleBack = () => {
    navigate(previousBackTarget.pathname, {
      state: previousBackTarget.state,
    });
  };

  const { data, isLoading, isFetching, isError, error } = useAttemptedGamificationList(
    {
      states_id: selectedStateId,
      category_id: selectedCategoryId,
      page: 1,
    },
    {
      enabled:
        Number.isFinite(selectedStateId) &&
        selectedStateId > 0 &&
        Number.isFinite(selectedCategoryId) &&
        selectedCategoryId > 0,
    }
  );

  useEffect(() => {
    persistGamificationFlowState({
      ...flowState,
      userCoins: Number(data?.userCoins || flowState?.userCoins || 0),
      user_coins: Number(data?.userCoins || flowState?.user_coins || 0),
      rank: Number(data?.rank || flowState?.rank || 0),
      score: Number(data?.score || flowState?.score || 0),
      gamificationBackTarget: previousBackTarget,
    });
  }, [data?.rank, data?.score, data?.userCoins, flowState, previousBackTarget]);

  const leaderboardState = useMemo(
    () => ({
      ...flowState,
      userCoins: Number(data?.userCoins || flowState?.userCoins || 0),
      user_coins: Number(data?.userCoins || flowState?.user_coins || 0),
      rank: Number(data?.rank || flowState?.rank || 0),
      score: Number(data?.score || flowState?.score || 0),
      selectedState: flowState?.selectedState || { state_id: selectedStateId },
      selectedCategory: flowState?.selectedCategory || { category_id: selectedCategoryId },
      state_id: selectedStateId,
      category_id: selectedCategoryId,
      gamificationBackTarget: buildGamificationBackTarget(GAMIFICATION_ROUTES.start, {
        ...flowState,
        userCoins: Number(data?.userCoins || flowState?.userCoins || 0),
        user_coins: Number(data?.userCoins || flowState?.user_coins || 0),
        rank: Number(data?.rank || flowState?.rank || 0),
        score: Number(data?.score || flowState?.score || 0),
        selectedState: flowState?.selectedState || { state_id: selectedStateId },
        selectedCategory: flowState?.selectedCategory || { category_id: selectedCategoryId },
        state_id: selectedStateId,
        category_id: selectedCategoryId,
        gamificationBackTarget: previousBackTarget,
      }),
    }),
    [data?.rank, data?.score, data?.userCoins, flowState, previousBackTarget, selectedCategoryId, selectedStateId]
  );

  const { onAction, notice, dismissNotice, error: actionError, rejoiningRoomId } =
    useGamificationAttemptActions(leaderboardState);

  const isPending = isLoading || isFetching;
  const loadError = isError
    ? error?.response?.data?.message || error?.message || "Unable to load gamification attempts."
    : "";
  const headerTitle = data?.categoryName || flowState?.selectedCategory?.title || "Gamification";

  return (
    <>
      <style>{`
        .homeDashboard-wrapper .page-content {
          padding-right: 0;
        }

        .homeDashboard-wrapper .main-content {
          box-sizing: border-box;
          padding-bottom: 35px;
          min-height: 0;
        }
        .rapid-fire-card {
          padding: 20px 0px;
        }
      `}</style>
      <HomeDashboardLayout showHeader={false}>
        <div className="qbl-page qbankList-page page-padding start-gamification">
          <div className="start-quiz qbl-main instruction-main">
            <div className="hideProfile-page">
              <div className="categories-page-header mb-0">
                <div>
                  <button
                    type="button"
                    className="categories-back-button"
                    onClick={handleBack}
                    aria-label="Go back"
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </div>
                <div>
                  <h4 className="d-flex d-sm-none categories-page-title">{headerTitle}</h4>
                </div>
                <div>
                  <HeaderProfileSection />
                </div>
              </div>
            </div>

            {isPending ? (
              <>
                <GamificationHeaderShimmer />
              </>
            ) : null}

            {!isPending && loadError ? (
              <p className="choose-state__status choose-state__status--error">{loadError}</p>
            ) : null}

            {!isPending && !loadError ? (
              <>
                <StartGamificationHeader
                  title={data?.categoryName || "Gamification"}
                  description={data?.description || "Compete, Learn & Improve Daily"}
                  rank={Number(data?.rank || 0)}
                  coins={Number(data?.userCoins || 0)}
                  score={Number(data?.score || 0)}
                  onLeaderboardClick={() =>
                    navigate(GAMIFICATION_ROUTES.leaderboard, {
                      state: leaderboardState,
                    })
                  }
                  onCoinsClick={() => setIsCoinOptionsOpen(true)}
                  onStartBattle={() =>
                    navigate(GAMIFICATION_ROUTES.selectBattle, {
                      state: leaderboardState,
                    })
                  }
                />
                {actionError ? <p className="choose-state__status choose-state__status--error" role="alert">{actionError}</p> : null}
                {data?.attempts?.length ? (
                  <BattleCard
                    attempts={data.attempts}
                    compactMobile
                    onViewAll={() => {
                      queryClient.setQueryData(
                        ["gamificationAttempts", selectedStateId, selectedCategoryId],
                        { pages: [data], pageParams: [1] }
                      );
                      navigate(GAMIFICATION_ROUTES.attempts, {
                        state: { flowState: { ...leaderboardState, homeBackTarget: previousBackTarget }, initialData: data },
                      });
                    }}
                    onAction={onAction}
                    rejoiningRoomId={rejoiningRoomId}
                  />
                ) : null}
              </>
            ) : null}
          </div>

          {/* Fixed right sidebar */}
          <MyWalletSidebar withTopOffset={false} />
        </div>

        <GamificationActivityDialog notice={notice} onClose={dismissNotice} />

        <CoinChooseOption
          open={isCoinOptionsOpen}
          onClose={() => setIsCoinOptionsOpen(false)}
          onStoreClick={() => {
            setIsCoinOptionsOpen(false);
            navigate(GAMIFICATION_ROUTES.coinStore, {
              state: leaderboardState,
            });
          }}
          onHistoryClick={() => {
            setIsCoinOptionsOpen(false);
            navigate(GAMIFICATION_ROUTES.coinHistory, {
              state: leaderboardState,
            });
          }}
        />
      </HomeDashboardLayout>
    </>
  );
};

export default StartGamification;



